import React, { useMemo, useState } from "react";
import {
    useGetAdminNewsQuery,
    useDeleteAdminNewsMutation,
    useGetAdminNewsItemQuery,
} from "../store/api";
import { useSearchParams, useNavigate } from "react-router-dom";
import NewsCategoriesModal from "../components/news/NewsCategoriesModal";

const AdminNewsPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get("page") || 1);
    const per_page = Number(searchParams.get("per_page") || 20);
    const lang = searchParams.get("lang") || "ru";

    const { data, isLoading, isFetching } = useGetAdminNewsQuery({
        page,
        per_page,
    });
    const [deleteNews, { isLoading: isDeleting }] =
        useDeleteAdminNewsMutation();
    const [viewId, setViewId] = useState<number | null>(null);
    const navigate = useNavigate();
    const { data: detail, isFetching: isDetailFetching } =
        useGetAdminNewsItemQuery(viewId as number, {
            skip: viewId === null,
        });
    const [viewLang, setViewLang] = useState<string>(lang);
    const [categoriesOpen, setCategoriesOpen] = useState(false);

    const pagination = useMemo(() => {
        const current = data?.meta?.current_page || 1;
        const last = data?.meta?.last_page || 1;
        return { current, last };
    }, [data]);

    const goToPage = (p: number) => {
        const params = new URLSearchParams(searchParams);
        params.set("page", String(p));
        setSearchParams(params);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Удалить новость?")) return;
        try {
            await deleteNews(id).unwrap();
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-semibold">Новости</h1>
                <div className="flex items-center gap-2">
                    <button
                        className="px-4 py-2 bg-gray-100 rounded"
                        onClick={() => setCategoriesOpen(true)}
                    >
                        Категории
                    </button>
                    <button
                        className="px-4 py-2 bg-blue-600 text-white rounded"
                        onClick={() => navigate("/dashboard/news/create")}
                    >
                        Добавить
                    </button>
                </div>
            </div>

            {(isLoading || isFetching) && (
                <div className="py-10 text-center text-gray-500 dark:text-gray-400">
                    Загрузка...
                </div>
            )}

            {data && (
                <>
                    <div className="overflow-x-auto bg-white rounded shadow">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        ID
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Заголовок
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Опубликовано
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Видео
                                    </th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Действия
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                                {data.data.map((n) => {
                                    const newsId = n.id || n.alny_news_id;
                                    const title =
                                        n.translations?.ru?.title ||
                                        n.translations?.en?.title ||
                                        "—";
                                    return (
                                        <tr key={newsId}>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                                {newsId}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {title}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                                {n.published_at
                                                    ? new Date(
                                                          n.published_at
                                                      ).toLocaleDateString(
                                                          "ru-RU"
                                                      )
                                                    : "—"}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                                {n.alny_news_video === 1
                                                    ? "Да"
                                                    : "Нет"}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap text-sm text-right">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <button
                                                        className="px-2 py-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                                        onClick={() =>
                                                            setViewId(newsId)
                                                        }
                                                        title="Смотреть"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-5 w-5"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                            />
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                            />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        className="px-2 py-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                                        onClick={() =>
                                                            navigate(
                                                                `/dashboard/news/${newsId}/edit`
                                                            )
                                                        }
                                                        title="Редактировать"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-5 w-5"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                            />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        className="px-2 py-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        onClick={() =>
                                                            handleDelete(newsId)
                                                        }
                                                        disabled={isDeleting}
                                                        title="Удалить"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-5 w-5"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                            />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                        <button
                            className="px-3 py-2 rounded bg-gray-100 dark:bg-gray-700 disabled:opacity-50"
                            onClick={() =>
                                goToPage(Math.max(1, pagination.current - 1))
                            }
                            disabled={pagination.current <= 1}
                        >
                            Назад
                        </button>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Стр. {pagination.current} из {pagination.last}
                        </div>
                        <button
                            className="px-3 py-2 rounded bg-gray-100 dark:bg-gray-700 disabled:opacity-50"
                            onClick={() =>
                                goToPage(
                                    Math.min(
                                        pagination.last,
                                        pagination.current + 1
                                    )
                                )
                            }
                            disabled={pagination.current >= pagination.last}
                        >
                            Вперед
                        </button>
                    </div>
                </>
            )}

            <NewsCategoriesModal
                open={categoriesOpen}
                onClose={() => setCategoriesOpen(false)}
            />

            {viewId !== null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => setViewId(null)}
                    ></div>
                    <div className="relative bg-white w-full max-w-2xl rounded shadow-lg p-6 z-10">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold">
                                Новость #{viewId}
                            </h2>
                            <button
                                className="p-2"
                                onClick={() => setViewId(null)}
                            >
                                ✕
                            </button>
                        </div>
                        {isDetailFetching && (
                            <div className="py-6 text-center text-gray-500 dark:text-gray-400">
                                Загрузка...
                            </div>
                        )}
                        {detail && (
                            <div className="space-y-4">
                                <div>
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                            Заголовок ({viewLang})
                                        </div>
                                        <select
                                            className="border rounded px-2 py-1 text-sm"
                                            value={viewLang}
                                            onChange={(e) =>
                                                setViewLang(e.target.value)
                                            }
                                        >
                                            {Object.keys(
                                                detail.translations || { ru: {} }
                                            ).map((code) => (
                                                <option key={code} value={code}>
                                                    {code}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="text-lg font-medium">
                                        {detail.translations?.[viewLang]
                                            ?.title ||
                                            detail.translations?.ru?.title ||
                                            "—"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                        Текст ({viewLang})
                                    </div>
                                    <div className="whitespace-pre-wrap">
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html:
                                                    detail.translations?.[
                                                        viewLang
                                                    ]?.body_html ||
                                                    detail.translations?.ru
                                                        ?.body_html ||
                                                    "—",
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                            Видео
                                        </div>
                                        <div>
                                            {detail.alny_news_video === 1
                                                ? "Да"
                                                : "Нет"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                            Видео URL
                                        </div>
                                        <a
                                            href={
                                                detail.alny_news_video_url ||
                                                "#"
                                            }
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-600 hover:underline"
                                        >
                                            {detail.alny_news_video_url || "—"}
                                        </a>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                            Постер
                                        </div>
                                        {(detail as any)?.data?.poster ||
                                        detail.poster ? (
                                            <img
                                                src={
                                                    (detail as any)?.data
                                                        ?.poster ||
                                                    detail.poster
                                                }
                                                alt="poster"
                                                className="h-32 w-auto rounded border"
                                            />
                                        ) : (
                                            <div>—</div>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm text-gray-500 dark:text-gray-400">
                                    <div>
                                        Создано:{" "}
                                        {(detail as any)?.data?.created_at ||
                                            detail.created_at}
                                    </div>
                                    <div>
                                        Обновлено:{" "}
                                        {(detail as any)?.data?.updated_at ||
                                            detail.updated_at}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminNewsPage;
